import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Action, z } from '@mkcore/shared';
import { toast } from '@mkcore/ui';
import { ApiError, call, newIdempotencyKey } from './api';

/** How long a toast with an undo action stays: long enough to read it and reach the button (the default is 4 s). */
export const UNDO_TOAST_MS = 10_000;

/** A user-readable message for a problem: a translation of its code, else the server's detail. */
export function useErrorMessage() {
  const { t, i18n } = useTranslation();
  return (e: unknown): string => {
    if (e instanceof ApiError) {
      const key = `errors.${e.code}`;
      if (i18n.exists(key)) return t(key);
      return e.problem.detail ?? t('errors.generic');
    }
    return t('errors.generic');
  };
}

/**
 * Mutation on an action. Each call gets its own Idempotency-Key, created once per user action, so a retry by the
 * query client or a double click never books twice (docs/01-architectuur/11-frontend.md). Errors show as a toast
 * unless the form handles them.
 */
export function useActionMutation<I extends z.ZodTypeAny, O extends z.ZodTypeAny>(
  action: Action<I, O>,
  opts: {
    invalidate?: QueryKey[];
    success?: string | ((out: z.output<O>) => string);
    onSuccess?: (out: z.output<O>) => void;
    silentErrors?: boolean;
  } = {},
) {
  const qc = useQueryClient();
  const message = useErrorMessage();
  return useMutation({
    mutationFn: (v: { input: z.input<I>; key?: string; ifMatch?: number }) =>
      call(action, v.input, { idempotencyKey: v.key ?? newIdempotencyKey(), ifMatch: v.ifMatch }),
    onSuccess: (out) => {
      for (const k of opts.invalidate ?? [[action.name.split('.')[0]!]]) void qc.invalidateQueries({ queryKey: k });
      if (opts.success) toast.success(typeof opts.success === 'function' ? opts.success(out) : opts.success);
      opts.onSuccess?.(out);
    },
    onError: (e) => {
      if (!opts.silentErrors) toast.error(message(e));
    },
  });
}
